import React from 'react'
import { v } from './landscapes/shared'
import Bay from './landscapes/bay'
import Desert from './landscapes/desert'
import Forest from './landscapes/forest'

// The shell of the scene: sky, stars, clouds, the sun on its arc, then one of
// the landscapes, then the click target. Everything that
// varies by time of day or by load is a CSS custom property (colours,
// --sun-alt, --stars, the cloud scatter), so the markup is identical on the
// server and the client.

export const LANDSCAPES = { bay: Bay, desert: Desert, forest: Forest }
export const SCENES = Object.keys(LANDSCAPES)

const STARS = (() => {
  let seed = 20260906
  const rnd = () => {
    seed = (seed * 1664525 + 1013904223) % 4294967296
    return seed / 4294967296
  }
  return Array.from({ length: 40 }, () => ({
    cx: (rnd() * 800).toFixed(1),
    cy: (rnd() * 235).toFixed(1),
    r: (0.5 + rnd() * 0.9).toFixed(2),
    o: (0.35 + rnd() * 0.6).toFixed(2),
  }))
})()

const Scene = ({ variant = 'hero', landscape = 'bay', onSunActivate, onSunHover, onSunLeave, sunRef, hitRef, glowRef }) => {
  const Active = LANDSCAPES[landscape] || Bay

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
      e.preventDefault()
      onSunActivate(e)
    }
  }

  return (
    <svg
      className="sky-scene"
      viewBox="0 0 800 500"
      preserveAspectRatio="xMidYMid meet"
      role="group"
      aria-label={Active.label}
    >
      <defs>
        <linearGradient id="sky-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: v('sky0') }} />
          <stop offset="28%" style={{ stopColor: v('sky1') }} />
          <stop offset="52%" style={{ stopColor: v('sky2') }} />
          <stop offset="72%" style={{ stopColor: v('sky3') }} />
          <stop offset="88%" style={{ stopColor: v('sky4') }} />
          <stop offset="100%" style={{ stopColor: v('sky5') }} />
        </linearGradient>
        <linearGradient id="sky-sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: v('sea0') }} />
          <stop offset="12%" style={{ stopColor: v('sea1') }} />
          <stop offset="45%" style={{ stopColor: v('sea2') }} />
          <stop offset="100%" style={{ stopColor: v('sea3') }} />
        </linearGradient>
        <linearGradient id="sky-sand" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: v('sand0') }} />
          <stop offset="100%" style={{ stopColor: v('sand1') }} />
        </linearGradient>
        <radialGradient id="sky-halo">
          <stop offset="0%" style={{ stopColor: v('halo0') }} stopOpacity="0.85" />
          <stop offset="55%" style={{ stopColor: v('halo1') }} stopOpacity="0.28" />
          <stop offset="100%" style={{ stopColor: v('halo2') }} stopOpacity="0" />
        </radialGradient>
        <radialGradient id="sky-sundisc">
          <stop offset="0%" style={{ stopColor: v('sun0') }} />
          <stop offset="70%" style={{ stopColor: v('sun1') }} />
          <stop offset="100%" style={{ stopColor: v('sun2') }} />
        </radialGradient>
        <linearGradient id="sky-swash" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: v('swash') }} stopOpacity="0" />
          <stop offset="65%" style={{ stopColor: v('swash') }} stopOpacity="0.3" />
          <stop offset="100%" style={{ stopColor: v('swash') }} stopOpacity="0.62" />
        </linearGradient>
        <linearGradient id="sky-damp" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" style={{ stopColor: v('sky2') }} stopOpacity="0" />
          <stop offset="45%" style={{ stopColor: v('sky2') }} stopOpacity="1" />
          <stop offset="55%" style={{ stopColor: v('sea2') }} stopOpacity="1" />
          <stop offset="100%" style={{ stopColor: v('sea2') }} stopOpacity="0" />
        </linearGradient>
        {/* Vignette: wide radius so it only settles into the corners. */}
        <radialGradient id="sky-vignette" r="82%">
          <stop offset="70%" stopColor="#000" stopOpacity="0" />
          <stop offset="100%" style={{ stopColor: v('vig') }} stopOpacity="0.14" />
        </radialGradient>
        <clipPath id="sky-frame">
          <rect x="0" y="0" width="800" height="500" />
        </clipPath>
      </defs>

      <g clipPath="url(#sky-frame)">
        <rect width="800" height="500" fill="url(#sky-sky)" />

        <g className="stars" style={{ fill: v('sun0') }}>
          {STARS.map((s, i) => (
            <circle key={i} cx={s.cx} cy={s.cy} r={s.r} opacity={s.o} />
          ))}
        </g>

        {/* Strata clouds, lit from below; they drift and swell. The outer group
            carries the per-load scatter (from --cloud-a/b/c, rolled by the
            head script), since .cloud's transform is spoken for by the drift
            animation. */}
        <g className="cloud-pos cloud-pos-a">
          <g className="cloud" style={{ animationDuration: '86s' }}>
            <ellipse cx="205" cy="118" rx="118" ry="6.5" style={{ fill: v('cloud0') }} opacity="0.32" />
            <ellipse cx="150" cy="136" rx="72" ry="5" style={{ fill: v('cloud0') }} opacity="0.24" />
          </g>
        </g>
        <g className="cloud-pos cloud-pos-b">
          <g className="cloud" style={{ animationDuration: '104s', animationDelay: '-30s' }}>
            <ellipse cx="585" cy="163" rx="142" ry="7.5" style={{ fill: v('cloud1') }} opacity="0.36" />
            <ellipse cx="655" cy="182" rx="86" ry="5.5" style={{ fill: v('cloud2') }} opacity="0.28" />
          </g>
        </g>
        <g className="cloud-pos cloud-pos-c">
          <g className="cloud" style={{ animationDuration: '72s', animationDelay: '-50s' }}>
            <ellipse cx="380" cy="212" rx="168" ry="6" style={{ fill: v('cloud3') }} opacity="0.3" />
          </g>
        </g>

        {/* The sun (or, in the night tokens, the moon): drawn whole, then the
            landscape crops it at the horizon. */}
        <g className="sunwrap">
          <g className="sunjiggle" ref={sunRef}>
            <circle className="glow" cx="440" cy="300" r="150" fill="url(#sky-halo)" />
            {/* Blooms outward on hover; otherwise invisible. */}
            <circle className="hover-glow" ref={glowRef} cx="440" cy="300" r="150" fill="url(#sky-halo)" opacity="0" />
            <circle cx="440" cy="300" r="52" fill="url(#sky-sundisc)" />
          </g>
        </g>

        {/* All four landscapes are in the markup; the stylesheet shows the one
            stamped on <html> as data-scene, so the pick is visible before first
            paint and the server and client markup never differ. */}
        {SCENES.map((name) => {
          const Landscape = LANDSCAPES[name]
          return (
            <g key={name} className="landscape" data-landscape={name}>
              <Landscape variant={variant} />
            </g>
          )
        })}

        <rect width="800" height="500" fill="url(#sky-vignette)" />

        {/* The click target rides the same arc as the sun, drawn last so the
            landscape can't cover it when the sun sits low. */}
        <g className="sunwrap">
          <circle
            ref={hitRef}
            className="sun-hit"
            cx="440" cy="300" r="70"
            fill="transparent"
            role="button"
            tabIndex={0}
            aria-label="Turn the hour: show the next time of day"
            onClick={onSunActivate}
            onKeyDown={onKeyDown}
            onPointerEnter={onSunHover}
            onPointerLeave={onSunLeave}
          />
          {/* Outer half of the focus ring: dark in every phase, so the ring
              reads against the bright halo. */}
          <circle className="sun-ring" cx="440" cy="300" r="74" fill="none" aria-hidden="true" />
        </g>
      </g>
    </svg>
  )
}

export default Scene
